import { useContext } from 'react';
import { copyTextToClipboard } from '../../utils/copy';
import { useToasts } from '../common/Toast';
import { GlobalContext } from '../contexts/GlobalContext';
import { EventWithData } from '../../../../../typings/Event.interface';
import { Request } from '../../../../../typings/Request.interface';
import { APISource } from '../../../../../typings/Source.interface';

const useCopyAsCurl = (
  props: { event: EventWithData } | { request: Request; source: APISource },
) => {
  const { HookdeckAPI } = useContext(GlobalContext);
  const { addToast } = useToasts();

  // Call sites pass `event!` / `request!` while the SWR data may still be loading. Every
  // read below must stay null-safe on the root (`event?.` / `request?.`): the compiler
  // hoists the callbacks' dependency paths into reads that run during render, before the
  // loading guards, so a bare `event.data` throws while the sidebar is still loading.
  if ('event' in props) {
    const { event } = props;
    return async () => {
      const data = event?.data;
      const is_binary = data?.body_format === 'binary';
      const raw_body_response = is_binary
        ? null
        : await HookdeckAPI.events.getRawBody(event?.id as string);
      const raw_body = raw_body_response instanceof Blob ? null : raw_body_response?.body;

      const curl_snippet_lines = [`curl "${data?.url}"`] as string[];
      curl_snippet_lines.push(`-X ${data?.method?.toUpperCase()}`);
      Object.entries(data?.headers ?? {}).forEach(([key, value]) => {
        if (value != null) {
          curl_snippet_lines.push(`-H "${key}: ${value.replace(/"/g, '\\"')}"`);
        }
      });
      if (is_binary) {
        curl_snippet_lines.push('--data-binary @payload.bin');
      } else if (raw_body) {
        curl_snippet_lines.push(`-d "${raw_body.replace(/"/g, '\\"')}"`);
      }
      const curl_snippet = curl_snippet_lines.join(' \\\n');

      const success = await copyTextToClipboard(curl_snippet);
      if (success) {
        addToast('success', 'Copied cURL command to clipboard');
      }
    };
  }
  if ('request' in props) {
    const { request, source } = props;
    return async () => {
      const appendTrailingSlash = (url: string, path: string): string => {
        const parts = url.split('?');
        if (parts[0].endsWith('/')) {
          parts[0] = parts[0].slice(0, -1);
        }
        return `${parts[0]}${path}${parts.length > 1 ? parts[0] : ''}`;
      };

      const data = request?.data;
      const is_binary = data?.body_format === 'binary';
      const raw_body_response = is_binary
        ? null
        : await HookdeckAPI.requests.getRawBody(request?.id as string);
      const raw_body = raw_body_response instanceof Blob ? null : raw_body_response?.body;

      let url = source?.url || 'https://mock.hookdeck.com';

      if (data?.path) {
        url = appendTrailingSlash(url, data.path);
      }

      if (data?.query) {
        url = `${url}?${data?.query}`;
      }
      const curl_snippet_lines = [`curl "${url}"`] as string[];
      curl_snippet_lines.push(
        `-X ${(data?.headers['x-hookdeck-original-method'] || 'POST').toUpperCase()}`,
      );
      Object.entries(data?.headers ?? {}).forEach(([key, value]) => {
        if (value != null) {
          curl_snippet_lines.push(`-H "${key}: ${value.replace(/"/g, '\\"')}"`);
        }
      });
      if (is_binary) {
        curl_snippet_lines.push('--data-binary @payload.bin');
      } else if (raw_body) {
        curl_snippet_lines.push(`-d "${raw_body.replace(/"/g, '\\"')}"`);
      }
      const curl_snippet = curl_snippet_lines.join(' \\\n');

      const success = await copyTextToClipboard(curl_snippet);
      if (success) {
        addToast('success', 'Copied cURL command to clipboard');
      }
    };
  }
};

export default useCopyAsCurl;
